import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import { ToastService } from '@app/core/services/toast/toast.service';
import { IProductCentreFilterAttributeDto, IProductCentreFilterDto } from '@classictechsolutions/hubapi-transpiled-enums';
import { IProductCentreFilterMappedItem } from '@app/logic/product-centre/interfaces/i.product-centre-filter.mapped';
import { IProductCentreFilterLogicService } from '@app/logic/product-centre/interfaces/i.product-centre-filter.logic.service';
import { ProductCentreFilterLogicService } from '@app/logic/product-centre/product-centre-filter.logic.service';
import { ProductCentreFilterMappedItem } from '@app/logic/product-centre/product-centre-filter.mapped';
import { FormMode } from '@app/shared/enums/form';
import { Observable, take } from 'rxjs';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import {
    ProductCentreFilterAttributeDialogComponent
} from '@app/views/settings/pages/product-centre-configuration/product-centre-filter-attribute-dialog/product-centre-filter-attribute-dialog.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

interface IData {
    mappedItem: ProductCentreFilterMappedItem;
    otherData: IProductCentreFilterDto[];
}

@Component({
    selector: 'cb-product-centre-filter-dialog',
    templateUrl: './product-centre-filter-dialog.component.html',
    styleUrls: ['./product-centre-filter-dialog.component.scss'],
    standalone: false
})
export class ProductCentreFilterDialogComponent
    extends BaseDialogFormViewDirective<IProductCentreFilterDto, IProductCentreFilterMappedItem, IProductCentreFilterLogicService> implements OnInit {
    public static readonly MIN_WIDTH = '50%';

    public dataChanged = false;

    public readonly attributeColumns = [
        '',
        'Match Value',
        'Is Regular Expression',
        'Is Active',
        'Prerequisite Filter',
        'Prerequisite Filter Match Value',
        ''
    ];

    public dataSource: any[];
    public showActiveOnly = true;
    public reorderingEnabled: boolean;

    private _enoughAttributesToReorder = false;
    public get canReorderAttributes(): boolean {
        return this._enoughAttributesToReorder;
    };

    public get mappedItem(): ProductCentreFilterMappedItem {
        return this.data.mappedItem;
    }

    constructor(
        public readonly toastService: ToastService,
        public readonly dialogRef: MatDialogRef<ProductCentreFilterDialogComponent>,
        protected readonly productCentreLogicService: ProductCentreFilterLogicService,
        private readonly cbDialog: CbDialogService,
        @Inject(MAT_DIALOG_DATA) public readonly data: IData,
    ) {
        super(dialogRef, toastService);
    }

    public ngOnInit(): void {
        this.formMode = this.mappedItem.id ? FormMode.Edit : FormMode.Add;
        this.mappedItem.attributes = this.mappedItem.attributes || [];
        this._enoughAttributesToReorder = this.mappedItem.attributes.length > 1;
    }

    public drop(event: CdkDragDrop<IProductCentreFilterAttributeDto[]>) {
        if (event.previousIndex === event.currentIndex) return;

        moveItemInArray(this.mappedItem.attributes, event.previousIndex, event.currentIndex);
        this.mappedItem.attributes.forEach((attr, index) => attr.sortOrder = index);
        this.dataChanged = true;
    }

    public removeFilterAttribute(index: number): void {
        this.mappedItem.attributes.splice(index, 1);
        this._enoughAttributesToReorder = this.mappedItem.attributes.length > 1;
        this.dataChanged = true;
    }

    private manageAttribute(filterAttribute: IProductCentreFilterAttributeDto): Observable<any> {
        const data = {
            attribute: filterAttribute,
            availableFilters: this.data.otherData
        };
        return this.cbDialog
            .open(ProductCentreFilterAttributeDialogComponent, {
                data,
                minWidth: '45%',
            })
            .afterClosed();
    }

    public editAttribute(filterAttribute: IProductCentreFilterAttributeDto): void {
        this.manageAttribute(filterAttribute)
            .pipe(take(1)).subscribe((result) => {
                if (result) {
                    Object.assign(filterAttribute, result);
                    this.dataChanged = true;
                }
            });
    }

    public addAttribute(): void {
        this.manageAttribute(this.newFilterAttribute())
            .pipe(take(1)).subscribe((result) => {
                if (result) {
                    this.mappedItem.attributes.push(result);
                    this._enoughAttributesToReorder = this.mappedItem.attributes.length > 1;
                    this.dataChanged = true;
                }
            });
    }

    private newFilterAttribute(): IProductCentreFilterAttributeDto {
        return {
            productCentreFilterId: this.mappedItem.id,
            isActive: true,
            sortOrder: (this.mappedItem.attributes?.length ?? 0)
        } as IProductCentreFilterAttributeDto;
    }

    public getPrerequisiteFilterName(prerequisiteFilterMatchId: string): string {
        if (!prerequisiteFilterMatchId) {
            return '';
        }
        const filter = this.data.otherData.find(filter => filter.id === prerequisiteFilterMatchId);
        return filter ? filter.name : '';
    }
}
